/**
 * Collection Page Enhancements
 * Handles AJAX add-to-cart and dynamic product recommendations for high-conversion UX
 */

(function() {
  'use strict';

  // ============================================
  // AJAX Add to Cart - Toast Notification Flow
  // (Scoped to Collection Pages Only)
  // ============================================
  
  function initCollectionAddToCart() {
    // Find ALL collection grid containers (specific data attribute opt-in)
    const collectionContainers = document.querySelectorAll('[data-collection-ajax-cart]');
    if (collectionContainers.length === 0) return; // Not a collection page, skip
    
    // Bind to each container individually with per-container guards
    collectionContainers.forEach(function(collectionContainer) {
      // Guard: check if this specific container is already bound
      if (collectionContainer.dataset.ajaxCartBound === 'true') return;
      collectionContainer.dataset.ajaxCartBound = 'true';
      
      // Event delegation scoped to this collection container
      collectionContainer.addEventListener('submit', function(e) {
        const form = e.target;
        
        // Only handle product card forms within collection
        if (!form.matches('[data-product-form]')) return;
        
        e.preventDefault();
        
        const submitBtn = form.querySelector('[data-add-to-cart-btn]');
        if (!submitBtn || submitBtn.disabled) return;
      
      // Get form data
      const formData = new FormData(form);
      const productData = {
        id: form.closest('[data-product-id]')?.dataset.productId,
        title: form.closest('[data-product-title]')?.dataset.productTitle,
        image: form.closest('[data-product-image]')?.dataset.productImage
      };
      
      // Save original button state
      const originalText = submitBtn.querySelector('[data-btn-text]')?.textContent || 'Add to Cart';
      const buttonTextEl = submitBtn.querySelector('[data-btn-text]');
      
      // Loading state
      submitBtn.disabled = true;
      if (buttonTextEl) {
        buttonTextEl.textContent = 'Adding...';
      }
      submitBtn.classList.add('labratgifts-btn--loading');
      
      // AJAX POST to cart
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => Promise.reject(err));
        }
        return response.json();
      })
      .then(item => {
        // Success state
        if (buttonTextEl) {
          buttonTextEl.textContent = '✓ Added!';
        }
        submitBtn.classList.remove('labratgifts-btn--loading');
        submitBtn.classList.add('labratgifts-btn--success');
        
        // Update cart count - fetch fresh count from cart
        fetch(window.Shopify.routes.root + 'cart.js')
          .then(res => res.json())
          .then(cart => {
            updateCartCount(cart.item_count);
          })
          .catch(err => console.error('Error updating cart count:', err));
        
        // Dispatch cart:item-added event for toast notification
        const message = `${item.product_title} added to cart!`;
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: {
            item: item,
            message: message,
            productData: productData
          }
        }));
        
        // Trigger product recommendations insertion
        insertProductRecommendations(item.product_id, productData);
        
        // Reset button after 2 seconds
        setTimeout(() => {
          submitBtn.disabled = false;
          if (buttonTextEl) {
            buttonTextEl.textContent = originalText;
          }
          submitBtn.classList.remove('labratgifts-btn--success');
        }, 2000);
      })
      .catch(error => {
        // Error state
        console.error('Add to cart error:', error);
        
        submitBtn.disabled = false;
        if (buttonTextEl) {
          buttonTextEl.textContent = originalText;
        }
        submitBtn.classList.remove('labratgifts-btn--loading');
        
        // Show error toast
        const errorMessage = error.description || error.message || 'Unable to add to cart. Please try again.';
        if (window.toast) {
          window.toast.show(errorMessage, 'error');
        } else {
          alert(errorMessage);
        }
      });
      });
    });
  }

  // ============================================
  // Dynamic Product Recommendations
  // ============================================
  
  let recommendationsInserted = false; // Prevent duplicate insertions
  
  function insertProductRecommendations(productId, productData) {
    // Get container with settings
    const container = document.querySelector('[data-collection-ajax-cart]');
    if (!container) return;
    
    // Check if recommendations are enabled via theme settings
    const enabled = container.dataset.recommendationsEnabled === 'true';
    if (!enabled) return;
    
    // Prevent duplicate recommendation rows
    if (recommendationsInserted) return;
    
    // Get admin settings from data attributes
    const limit = parseInt(container.dataset.recommendationsLimit) || 4;
    
    // Fetch recommendations from Shopify API
    fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`)
      .then(response => response.json())
      .then(data => {
        if (!data.products || data.products.length === 0) return;
        
        // Find the product grid
        const productGrid = container.querySelector('[data-products-grid]');
        if (!productGrid) return;
        
        // Determine insertion point based on viewport and admin settings
        const isMobile = window.innerWidth < 768;
        const insertAfterDesktop = parseInt(container.dataset.recommendationsInsertDesktop) || 4;
        const insertAfterMobile = parseInt(container.dataset.recommendationsInsertMobile) || 6;
        const insertAfter = isMobile ? insertAfterMobile : insertAfterDesktop;
        
        // Get grid item wrappers (not product cards themselves)
        const gridItems = productGrid.querySelectorAll('[data-product-item]');
        
        if (gridItems.length < insertAfter) return; // Not enough products
        
        // Create recommendations row with admin settings
        const recommendationsRow = createRecommendationsRow(data.products, productData, container);
        
        // Insert after the Nth grid item (as a sibling, not inside)
        const insertionPoint = gridItems[insertAfter - 1];
        insertionPoint.after(recommendationsRow);
        
        recommendationsInserted = true;
      })
      .catch(error => {
        console.error('Error fetching recommendations:', error);
      });
  }
  
  function createRecommendationsRow(products, addedProductData, container) {
    // Get admin settings for text customization
    const title = container.dataset.recommendationsTitle || 'You might also like';
    const subtitleTemplate = container.dataset.recommendationsSubtitle || 'Based on __PRODUCT__';
    const subtitle = subtitleTemplate.replace('__PRODUCT__', addedProductData.title || 'your selection');
    const limit = parseInt(container.dataset.recommendationsLimit) || 4;
    
    // Create a full-width recommendation container
    const row = document.createElement('div');
    row.className = 'labratgifts-recommendations-row';
    row.dataset.recommendationsRow = 'true';
    
    // Create header with admin-customizable text (escaped for security)
    const header = document.createElement('div');
    header.className = 'labratgifts-recommendations-row__header';
    header.innerHTML = `
      <h3 class="labratgifts-recommendations-row__title">${escapeHtml(title)}</h3>
      <p class="labratgifts-recommendations-row__subtitle">${escapeHtml(subtitle)}</p>
    `;
    
    // Create products grid
    const grid = document.createElement('div');
    grid.className = 'labratgifts-recommendations-row__grid';
    
    // Generate product cards (use admin-defined limit)
    products.slice(0, limit).forEach(product => {
      const card = createRecommendationCard(product);
      grid.appendChild(card);
    });
    
    row.appendChild(header);
    row.appendChild(grid);
    
    return row;
  }
  
  function createRecommendationCard(product) {
    const card = document.createElement('div');
    card.className = 'labratgifts-recommendation-card';
    card.dataset.productId = product.id;
    
    // Calculate discount if compare_at_price exists
    let discountBadge = '';
    if (product.compare_at_price_min && product.compare_at_price_min > product.price_min) {
      const discount = Math.round(((product.compare_at_price_min - product.price_min) / product.compare_at_price_min) * 100);
      discountBadge = `<span class="labratgifts-badge labratgifts-badge--sale">${discount}% off</span>`;
    }
    
    // Get image URL (handle both object and string formats)
    const imageUrl = product.featured_image 
      ? (typeof product.featured_image === 'object' ? product.featured_image.url : product.featured_image)
      : null;
    
    card.innerHTML = `
      <a href="${product.url}" class="labratgifts-recommendation-card__link">
        <div class="labratgifts-recommendation-card__image">
          ${imageUrl ? 
            `<img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy">` : 
            '<div class="labratgifts-recommendation-card__placeholder">No image</div>'
          }
          ${discountBadge}
        </div>
        <div class="labratgifts-recommendation-card__info">
          <h4 class="labratgifts-recommendation-card__title">${escapeHtml(product.title)}</h4>
          <div class="labratgifts-recommendation-card__price">
            <span class="labratgifts-price">${formatMoney(product.price_min)}</span>
            ${product.compare_at_price_min && product.compare_at_price_min > product.price_min ? 
              `<span class="labratgifts-price labratgifts-price--compare">${formatMoney(product.compare_at_price_min)}</span>` : 
              ''
            }
          </div>
          <button type="button" class="labratgifts-btn labratgifts-btn--sm labratgifts-btn--primary labratgifts-recommendation-card__cta">
            Quick Add
          </button>
        </div>
      </a>
    `;
    
    // Add quick add functionality
    const quickAddBtn = card.querySelector('.labratgifts-recommendation-card__cta');
    quickAddBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      quickAddRecommendation(product, quickAddBtn);
    });
    
    return card;
  }
  
  function quickAddRecommendation(product, button) {
    if (button.disabled) return;
    
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';
    button.classList.add('labratgifts-btn--loading');
    
    // Add first available variant to cart
    const formData = new FormData();
    formData.append('id', product.variants[0].id);
    formData.append('quantity', 1);
    
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(item => {
      button.textContent = '✓ Added!';
      button.classList.remove('labratgifts-btn--loading');
      button.classList.add('labratgifts-btn--success');
      
      updateCartCount();
      
      // Show toast
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: {
          item: item,
          message: `${product.title} added to cart!`
        }
      }));
      
      setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
        button.classList.remove('labratgifts-btn--success');
      }, 2000);
    })
    .catch(error => {
      console.error('Quick add error:', error);
      button.disabled = false;
      button.textContent = originalText;
      button.classList.remove('labratgifts-btn--loading');
      
      if (window.toast) {
        window.toast.show('Unable to add product. Please try again.', 'error');
      }
    });
  }
  
  // Format money helper (cents to dollars)
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  
  // HTML escape helper for security
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ============================================
  // Update Cart Count Badge
  // ============================================
  function updateCartCount(itemCount) {
    // If count provided, use it directly; otherwise fetch it
    if (itemCount !== undefined) {
      const cartCounts = document.querySelectorAll('[data-cart-count]');
      cartCounts.forEach(el => {
        el.textContent = itemCount;
        if (itemCount > 0) {
          el.style.display = '';
          el.classList.add('visible');
        } else {
          el.classList.remove('visible');
        }
      });
    } else {
      fetch(window.Shopify.routes.root + 'cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartCounts = document.querySelectorAll('[data-cart-count]');
          cartCounts.forEach(el => {
            el.textContent = cart.item_count;
            if (cart.item_count > 0) {
              el.style.display = '';
              el.classList.add('visible');
            } else {
              el.classList.remove('visible');
            }
          });
        })
        .catch(error => {
          console.error('Error fetching cart:', error);
        });
    }
  }

  // ============================================
  // Initialize on DOM Ready & Section Reload
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionAddToCart);
  } else {
    initCollectionAddToCart();
  }
  
  // Re-bind after Shopify section reload (filters, pagination, theme editor)
  // The per-container guard prevents duplicate listeners
  document.addEventListener('shopify:section:load', function(event) {
    // Check if any collection grid containers were loaded in this section
    const newContainers = event.target.querySelectorAll('[data-collection-ajax-cart]');
    if (newContainers.length > 0) {
      initCollectionAddToCart(); // Will bind only unbound containers
    }
  });

  
})();

